import {
  TextButton,
  TextButtonColor,
} from 'components/uikit/buttons/TextButton';
import { useCallback, useMemo, useState } from 'react';
import AddIcon from 'assets/img/add_icon.svg?react';
import { QueryFactory } from 'services/api';
import styles from './VariablesList.module.scss';
import { VariableCard } from './components/variableCard/VariableCard';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { VariableAccessLevel } from './components/variable-modal-data';
import { Accordion } from 'components/uikit/Accordion.tsx';
import { SearchInput } from 'components/uikit/inputs/SearchInput.tsx';
import { triggerDownloadFileDialog } from 'helpers/file-helper.ts';
import BlockedVariablesList from 'assets/icons/blocked-variables-list.svg?react';
import { IconLabelView } from 'components/uikit/iconLabel/IconLabelView.tsx';
import {
  CreateVariableDto,
  OperationType,
  ProjectResourceType,
  SystemVariableType,
  UrlType,
  VariableListItemDto,
} from 'services/api/api-client';
import { VariableModal } from './components/variableModal/VariableModal';
import { queryClient } from 'services/api/query-client-helper';
import { useDocumentContext } from '../documents/documentContext/DocumentContext.tsx';
import { PermissionTextButton } from 'helpers/projectPermissions/components/PermissionTextButton.tsx';
import { permission } from 'helpers/projectPermissions/types/projectPermission.ts';
import { useHasProjectPermission } from 'helpers/projectPermissions/useHasProjectPermission.ts';
import { useVariableLockSync } from './useVariableLockSync';
import {
  useVariablesContext,
  VariablesProvider,
} from './variablesContext/VariablesContext.tsx';
import { SystemVariableAccordion } from './components/systemVariablesAccordion/SystemVariableAccoridion.tsx';
import { CascadingFilterButton } from 'components/uikit/filters/cascading/CascadingFilterButton';
import FilterIcon from 'assets/icons/filter_24.svg?react';
import ExpandIcon from 'assets/icons/expand-folders.svg?react';
import CollapseIcon from 'assets/icons/collapse-folders.svg?react';
import { UseQueryResult } from '@tanstack/react-query';

type VariableListProps = {
  projectId: number;
  revisionId: number;
  onVariableClick:
    | ((variableId: number, urlType: UrlType | null) => void)
    | undefined;
  onVariableUpdate: (
    variableId: number[],
    disableRefetch: boolean,
  ) => Promise<void>;
  documentId?: number;
  isDocumentEditingLocked: boolean;
};

export type VariablesByAccessLevel = {
  local: QueryFactory.VariableListItemDto[];
  general: QueryFactory.VariableListItemDto[];
};

type VariablesByType = {
  custom: VariablesByAccessLevel;
  system: VariablesByAccessLevel;
};

export const VariableList: React.FC<VariableListProps> = (props) => {
  const { documentQuery } = useDocumentContext();

  if (props.documentId && !documentQuery.data) return null;

  const storageKey = props.documentId
    ? `prefs_VariablesView_document_${documentQuery.data!.initialId}`
    : `prefs_VariablesView_project_${props.projectId}`;

  return (
    <VariablesProvider
      key={`${props.projectId}-${props.revisionId}-${props.documentId}`}
      projectId={props.projectId}
      revisionId={props.revisionId}
      storageKey={storageKey}
    >
      <VariableListContent {...props} documentQuery={documentQuery} />
    </VariablesProvider>
  );
};

const VariableListContent: React.FC<
  VariableListProps & {
    documentQuery: UseQueryResult<QueryFactory.DocumentDto, unknown>;
  }
> = ({
  documentId,
  revisionId,
  isDocumentEditingLocked,
  documentQuery,
  onVariableUpdate,
  onVariableClick,
  projectId,
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editVariableId, setEditVariableId] = useState<number>();
  const [viewVariableId, setViewVariableId] = useState<number | null>(null);

  const { setDocumentState } = useDocumentContext();
  const {
    refreshVariables,
    variablesQuery,
    allVariablesQuery,
    search,
    setSearch,
    filterConfig,
    filter,
    setFilter,
    isNarrowed,
    expanded,
    setIsExpanded,
    toggleExpanded,
  } = useVariablesContext();
  const isExpanded = expanded.manual || expanded.system;

  // eslint-disable-next-line prefer-const
  let [selectedAccessLevel, setSelectedAccessLevel] = useState<
    VariableAccessLevel | undefined
  >(documentId ? VariableAccessLevel.Local : VariableAccessLevel.General);
  const i18n = useScopedTranslation('Page.Variables');

  useVariableLockSync(
    variablesQuery.data?.data.map((v) => v.id) ?? [],
    revisionId,
    refreshVariables,
  );

  const hasPermission = useHasProjectPermission();
  const hasManageVariablePermission = hasPermission([
    permission(
      ProjectResourceType.Variable,
      OperationType.Update,
      OperationType.Delete,
    ),
  ]);
  const hasUpdateDocumentPermission = hasPermission([
    permission(ProjectResourceType.Document, OperationType.Update),
  ]);

  const ReadOnlyReason: string | null = useMemo(() => {
    if (isDocumentEditingLocked)
      return hasUpdateDocumentPermission
        ? i18n.t('blocked_variables_editor_lock')
        : null;

    if (documentQuery.data?.isReadOnly)
      return i18n.t('blocked_variables_readonly');

    return null;
  }, [isDocumentEditingLocked, documentQuery.data?.isReadOnly]);
  const isReadOnly = ReadOnlyReason !== null;

  const variables: VariablesByType = useMemo(() => {
    const local =
      variablesQuery.data?.data.filter(
        (v) => v.documentId === documentQuery.data?.initialId,
      ) ?? [];
    const general =
      variablesQuery.data?.data.filter((v) => !v.documentId) ?? [];

    const customFilter = (y: QueryFactory.VariableListItemDto[]) =>
      y?.filter((x) => !x.basedOnDocument?.id && !x.revisionId);
    const systemFilter = (y: QueryFactory.VariableListItemDto[]) =>
      y?.filter((x) => x.basedOnDocument?.id || x.revisionId);

    const result: VariablesByType = {
      system: {
        local: systemFilter(local),
        general: systemFilter(general),
      },
      custom: {
        local: customFilter(local),
        general: customFilter(general),
      },
    };

    updateTabs(result, isNarrowed);
    return result;
  }, [variablesQuery.data?.data, documentQuery.data, isNarrowed]);

  function updateTabs(variables: VariablesByType, isNarrowed: boolean) {
    const localLength =
      variables.system.local.length + variables.custom.local.length;
    const generalLength =
      variables.system.general.length + variables.custom.general.length;

    // If variables presented only in one tab, this tab should be opened
    if (isNarrowed && generalLength > 0 !== localLength > 0) {
      setSelectedAccessLevel(
        localLength > 0
          ? VariableAccessLevel.Local
          : VariableAccessLevel.General,
      );
    }
  }

  const exportVariable = async (id: number, revisionId: number) => {
    const file = await QueryFactory.VariableClient.export_(id, revisionId);
    const newBlob = new Blob([file.data], { type: file.data.type });
    triggerDownloadFileDialog(newBlob, file.fileName!);
  };

  const handleManualVersionUpdate = useCallback(
    async (variableId: number) => {
      const versionHistoryAndVersionVarsIds = allVariablesQuery.data?.data
        .filter(
          (v) =>
            v.basedOnDocument?.id === documentQuery.data?.initialId &&
            (v.systemVariableType === SystemVariableType.Version ||
              v.systemVariableType === SystemVariableType.VersionHistory),
        )
        .map((v) => v.id);
      const isVersionVariable =
        versionHistoryAndVersionVarsIds?.includes(variableId) ?? false;

      if (isVersionVariable && versionHistoryAndVersionVarsIds) {
        await onVariableUpdate(versionHistoryAndVersionVarsIds, true);
        return true;
      }

      return false;
    },
    [
      documentQuery.data?.initialId,
      allVariablesQuery.data?.data,
      onVariableUpdate,
    ],
  );

  const isLoading = variablesQuery.isLoading || documentQuery.isLoading;

  const renderVariableCard = (
    variable: VariableListItemDto,
    className?: string,
  ) => {
    return (
      <VariableCard
        key={variable.id}
        variable={variable}
        revisionId={revisionId}
        isReadOnly={isReadOnly}
        onEdit={(id) => {
          if (documentId)
            setDocumentState((prevState) => ({
              ...prevState,
              needToSave: true,
            }));

          setEditVariableId(id);
          setIsEditModalOpen(true);
        }}
        onView={(id) => setViewVariableId(id)}
        onVariableUpdate={async () => {
          await onVariableUpdate([variable.id], false);
        }}
        onVariableClick={
          onVariableClick
            ? () => onVariableClick?.(variable.id, variable.actualValue.urlType)
            : undefined
        }
        onVariableExport={exportVariable}
        projectId={projectId}
        documentId={documentId}
        variableManageAllowed={hasManageVariablePermission}
        className={className}
      />
    );
  };

  return (
    <div className={styles.container} data-test-id={'variable-list'}>
      <div className={styles.header}>
        <span>{i18n.t('variables')}</span>
        {!isReadOnly && (
          <PermissionTextButton
            testId="add-variable-button"
            className={styles.addButton}
            icon={<AddIcon />}
            tooltip={i18n.t('add_new_variable')}
            tooltipProps={{
              placement: 'top',
              PopperProps: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, -10],
                    },
                  },
                ],
              },
            }}
            color={TextButtonColor.Primary}
            onClick={() => setIsModalOpen(true)}
            permissions={[
              permission(ProjectResourceType.Variable, OperationType.Create),
            ]}
          />
        )}
      </div>
      {!isReadOnly ? (
        <>
          <div className={styles.filterBlock}>
            <SearchInput
              value={search}
              onChange={(e) => {
                setIsExpanded(true);
                setSearch(e.currentTarget.value);
              }}
            />

            {documentId && (
              <div className={styles.typeSelector}>
                <VariableTab
                  testId="local-variable-tab"
                  text={i18n.t('local_variables')}
                  isSelected={selectedAccessLevel === VariableAccessLevel.Local}
                  defaultClassName={styles.levelContainer}
                  selectedClassName={styles.levelContainerSelected}
                  onClick={() =>
                    setSelectedAccessLevel(VariableAccessLevel.Local)
                  }
                />
                <VariableTab
                  testId="general-variable-tab"
                  text={i18n.t('general_variables')}
                  isSelected={
                    selectedAccessLevel === VariableAccessLevel.General
                  }
                  defaultClassName={styles.levelContainer}
                  selectedClassName={styles.levelContainerSelected}
                  onClick={() =>
                    setSelectedAccessLevel(VariableAccessLevel.General)
                  }
                />
              </div>
            )}

            <div className={styles.variableListControls}>
              <CascadingFilterButton
                testId="variables-filter"
                title={i18n.t('CascadingVariableFilter.title')}
                root={filterConfig}
                value={filter}
                onChange={(newFilter) => {
                  setIsExpanded(true);
                  setFilter(newFilter);
                }}
                icon={<FilterIcon />}
              />
              <TextButton
                color={TextButtonColor.Primary}
                title={
                  isExpanded ? i18n.t('collapse_all') : i18n.t('expand_all')
                }
                icon={isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                onClick={() => setIsExpanded(!isExpanded)}
              />
            </div>
          </div>
          <div className={styles.variablesScrollWrapper}>
            <Accordion
              testId="manual-variable-list"
              title={i18n.t('custom_variables')}
              replacementText={i18n.t('no_variables')}
              expanded={expanded.manual}
              loading={isLoading}
              onExpandClick={() => toggleExpanded('manual', expanded.manual)}
              titleClassName={styles.accordionTitle}
            >
              {(selectedAccessLevel === VariableAccessLevel.Local
                ? variables?.custom.local
                : variables?.custom.general
              )?.map((v) => renderVariableCard(v))}
            </Accordion>
            <SystemVariableAccordion
              expanded={expanded}
              onToggle={toggleExpanded}
              selectedAccessLevel={selectedAccessLevel}
              variables={variables.system}
              isLoading={isLoading}
              renderVariableCard={renderVariableCard}
              titleClassName={styles.accordionTitle}
              initialDocumentId={documentQuery.data?.initialId}
              currentRevisionId={revisionId}
            />
          </div>
        </>
      ) : (
        <IconLabelView icon={<BlockedVariablesList />} label={ReadOnlyReason} />
      )}
      {editVariableId && (
        <VariableModal
          mode={'edit'}
          id={editVariableId}
          revisionId={revisionId}
          initialDocumentId={documentQuery.data?.initialId!}
          documentId={documentId}
          isOpen={isEditModalOpen}
          onClose={async (isSaved) => {
            setIsEditModalOpen(false);
            if (isSaved && !(await handleManualVersionUpdate(editVariableId!)))
              await onVariableUpdate([editVariableId!], true);
          }}
          onSubmit={async (data) => {
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.RevisionQuery.getQueryKey(revisionId),
            });
            await QueryFactory.VariableClient.patch(
              editVariableId,
              {
                ...data,
                actualValue: data.actualValue.value,
                alternateValue: data.actualValue.alternateValue,
                variableType: data.actualValue.variableType,
                urlType: data.actualValue.urlType,
              },
              revisionId,
            );

            await Promise.all([
              queryClient.invalidateQueries({
                queryKey: QueryFactory.DocumentQuery.searchQueryKey(),
              }),
              refreshVariables(),
              queryClient.invalidateQueries({
                queryKey:
                  QueryFactory.VariableQuery.getQueryKey(editVariableId),
              }),
            ]);
          }}
        />
      )}

      {viewVariableId !== null && (
        <VariableModal
          mode={'view'}
          id={viewVariableId}
          revisionId={revisionId}
          initialDocumentId={documentQuery.data?.initialId!}
          documentId={documentId}
          isOpen={viewVariableId !== null}
          onClose={() => setViewVariableId(null)}
        />
      )}

      <VariableModal
        mode={'create'}
        revisionId={revisionId}
        initialDocumentId={documentQuery.data?.initialId!}
        documentId={
          selectedAccessLevel === VariableAccessLevel.Local
            ? documentQuery.data?.initialId!
            : undefined
        }
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={async (data) => {
          const createdVariable: CreateVariableDto = {
            ...data,
            isTracked: false,
            projectId,
            revisionId,
            url: data.actualUrl ?? '',
            value: data.actualValue.value,
            variableType: data.actualValue.variableType,
            urlType: data.actualValue.urlType,
            systemVariableType: null,
            basedOnRevisionId: null,
            basedOnDocumentId: data.systemVariable?.basedOnDocumentId ?? null,
          };
          await QueryFactory.VariableClient.create(createdVariable);
          await refreshVariables();
        }}
      />
    </div>
  );
};

type VariableTabProps = {
  testId?: string;
  text: string;
  isSelected: boolean;
  defaultClassName: string;
  selectedClassName: string;
  onClick: () => void;
};

const VariableTab: React.FC<VariableTabProps> = (props) => {
  return (
    <div
      data-test-id={props.testId}
      className={
        props.isSelected ? props.selectedClassName : props.defaultClassName
      }
      onClick={() => props.onClick()}
    >
      {props.text}
    </div>
  );
};
